import { ClassScheduleSettings } from '../services/sectionLayoutService';
import { AcademicTerm, AnnualTimetableCell, DayKind, Track } from '../types/domain';
import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import { deviceCacheStorage } from './deviceCacheStorage';

const ANNUAL_PROGRESS_CACHE_VERSION = 'v2';

export type OffDayByDate = Record<string, { kind: DayKind; note?: string }>;

export type AnnualProgressDataCacheState = {
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  /** 공휴·휴업·방학 등 `getCalendarDays`로 비수업 취급할 YYYY-MM-DD(하루) */
  holidayDays: string[];
  /** `holidayDays`에 포함된 날의 세부 종류(표시·라벨용) */
  offDayByDate?: OffDayByDate;
  /**
   * 학기 달력으로 공휴/휴업/방학을 한 번 반영해 `holidayDays`·`offDayByDate`를 채움.
   * (구 캐시: false → 재조회로 휴업일 등 포함)
   */
  offDaysHydrated?: boolean;
  cellsByKey: Record<string, AnnualTimetableCell>;
  holidayHydrated: boolean;
  cellsHydrated: boolean;
};

type AnnualProgressDataCachePayload = AnnualProgressDataCacheState & {
  savedAt: number;
};

function storageKey(workspaceId: string, academicYear: number, termNumber: 1 | 2): string {
  return ['cp.annualProgressData', ANNUAL_PROGRESS_CACHE_VERSION, workspaceId, academicYear, termNumber].join('.');
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function normalizeStringArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((value): value is string => typeof value === 'string');
}

function normalizeCellsByKey(raw: unknown): Record<string, AnnualTimetableCell> {
  if (!isRecord(raw)) return {};
  return Object.entries(raw).reduce<Record<string, AnnualTimetableCell>>((acc, [key, value]) => {
    if (value && typeof value === 'object') acc[key] = value as AnnualTimetableCell;
    return acc;
  }, {});
}

const OFF_DAY_KINDS: DayKind[] = ['holiday', 'closure', 'vacation'];

function isDayKind(v: string): v is DayKind {
  return OFF_DAY_KINDS.includes(v as DayKind);
}

function normalizeOffDayByDate(raw: unknown): OffDayByDate {
  if (!isRecord(raw)) return {};
  const out: OffDayByDate = {};
  Object.entries(raw).forEach(([date, v]) => {
    if (typeof date !== 'string' || !date.trim()) return;
    if (!v || typeof v !== 'object' || !('kind' in v)) return;
    const kind = typeof (v as { kind?: unknown }).kind === 'string' ? String((v as { kind: string }).kind) : '';
    if (!isDayKind(kind)) return;
    const noteRaw = (v as { note?: unknown }).note;
    const note = typeof noteRaw === 'string' ? noteRaw.trim() : undefined;
    out[date] = { kind, ...(note ? { note } : {}) };
  });
  return out;
}

export function loadAnnualProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2
): { found: boolean; state: AnnualProgressDataCacheState } {
  const emptyState: AnnualProgressDataCacheState = {
    terms: [],
    schedule: null,
    tracks: [],
    regularColors: [],
    dedicatedColors: [],
    baseExcludeColors: [],
    holidayDays: [],
    offDayByDate: {},
    offDaysHydrated: false,
    cellsByKey: {},
    holidayHydrated: false,
    cellsHydrated: false,
  };
  if (!workspaceId || typeof window === 'undefined') {
    return { found: false, state: emptyState };
  }
  try {
    const raw = deviceCacheStorage.getItem(storageKey(workspaceId, academicYear, termNumber));
    if (!raw) return { found: false, state: emptyState };
    const parsed = JSON.parse(raw) as Partial<AnnualProgressDataCachePayload> | null;
    if (!parsed || typeof parsed !== 'object') return { found: false, state: emptyState };
    return {
      found: true,
      state: {
        terms: Array.isArray(parsed.terms) ? (parsed.terms as AcademicTerm[]) : [],
        schedule: (parsed.schedule as ClassScheduleSettings | null) ?? null,
        tracks: Array.isArray(parsed.tracks) ? (parsed.tracks as Track[]) : [],
        regularColors: normalizeStringArray(parsed.regularColors),
        dedicatedColors: normalizeStringArray(parsed.dedicatedColors),
        baseExcludeColors: normalizeStringArray(parsed.baseExcludeColors),
        holidayDays: normalizeStringArray(parsed.holidayDays),
        offDayByDate: normalizeOffDayByDate(parsed.offDayByDate),
        offDaysHydrated: !!parsed.offDaysHydrated,
        cellsByKey: {},
        holidayHydrated: !!parsed.holidayHydrated,
        cellsHydrated:
          !!parsed.cellsHydrated || Object.keys(normalizeCellsByKey(parsed.cellsByKey)).length > 0,
      },
    };
  } catch {
    return { found: false, state: emptyState };
  }
}

export function saveAnnualProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  state: AnnualProgressDataCacheState
): void {
  if (!workspaceId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  const write = () => {
    const payload: AnnualProgressDataCachePayload = {
      ...state,
      /** 학기 셀 본문은 `cp.annualTimetableCells.*` per-grade LS에만 둠(aggregate 중복·quota 방지) */
      cellsByKey: {},
      savedAt: Date.now(),
    };
    deviceCacheStorage.setItem(storageKey(workspaceId, academicYear, termNumber), JSON.stringify(payload));
  };
  try {
    write();
  } catch {
    compactAnnualProgressDataCachesForWorkspace(workspaceId);
    try {
      write();
    } catch {
      // ignore quota / private mode write errors
    }
  }
}

/** 구 aggregate에 embedded 된 대용량 `cellsByKey` 제거(quota 회복) */
export function compactAnnualProgressDataCachesForWorkspace(workspaceId: string): void {
  if (!workspaceId || typeof window === 'undefined') return;
  const prefix = `${['cp.annualProgressData', ANNUAL_PROGRESS_CACHE_VERSION, workspaceId].join('.')}.`;
  const keysToRewrite: string[] = [];
  try {
    for (let i = 0; i < deviceCacheStorage.length; i += 1) {
      const key = deviceCacheStorage.key(i);
      if (key?.startsWith(prefix)) keysToRewrite.push(key);
    }
    keysToRewrite.forEach((key) => {
      const raw = deviceCacheStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<AnnualProgressDataCachePayload> | null;
      if (!parsed || typeof parsed !== 'object') return;
      const hadCells = Object.keys(normalizeCellsByKey(parsed.cellsByKey)).length > 0;
      if (!hadCells) return;
      const payload: AnnualProgressDataCachePayload = {
        terms: Array.isArray(parsed.terms) ? (parsed.terms as AcademicTerm[]) : [],
        schedule: (parsed.schedule as ClassScheduleSettings | null) ?? null,
        tracks: Array.isArray(parsed.tracks) ? (parsed.tracks as Track[]) : [],
        regularColors: normalizeStringArray(parsed.regularColors),
        dedicatedColors: normalizeStringArray(parsed.dedicatedColors),
        baseExcludeColors: normalizeStringArray(parsed.baseExcludeColors),
        holidayDays: normalizeStringArray(parsed.holidayDays),
        offDayByDate: normalizeOffDayByDate(parsed.offDayByDate),
        offDaysHydrated: !!parsed.offDaysHydrated,
        cellsByKey: {},
        holidayHydrated: !!parsed.holidayHydrated,
        cellsHydrated: !!parsed.cellsHydrated || hadCells,
        savedAt: Date.now(),
      };
      deviceCacheStorage.setItem(key, JSON.stringify(payload));
    });
  } catch {
    // ignore compaction failures
  }
}

/**
 * 연간진도표·대시보드: `slotKind === 'holiday'`(공휴/휴업/방학)일 때 시간표 칸에 넣는 표시 문자열.
 * 학사 달력 메모가 있으면 메모를, 없으면 종류별 기본 라벨.
 */
export function timetableTextForOffDayRow(
  dateIso: string,
  offDayByDate: OffDayByDate | undefined
): string {
  const o = (offDayByDate && dateIso && offDayByDate[dateIso]) || undefined;
  const note = o?.note?.trim();
  if (note) return note;
  if (o?.kind === 'closure') return '휴업일';
  if (o?.kind === 'vacation') return '방학';
  if (o?.kind === 'holiday') return '공휴일';
  return '공휴일';
}

export function clearAnnualProgressDataCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2
): void {
  if (!workspaceId || typeof window === 'undefined') return;
  try {
    deviceCacheStorage.removeItem(storageKey(workspaceId, academicYear, termNumber));
  } catch {
    // ignore storage removal failures
  }
}
